/**
 * 🪙 Chits Ticker Pill — Header chip showing current Chits + flashing +X
 * delta when Chits go up (harvest, arena win, login, etc).
 *
 * Polls /api/chits/state every 8s. When the balance increases vs last poll,
 * shows a fuchsia +X chip that fades after 4s. Pure cosmetic — no backend
 * changes required.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChitsTickerPill({ className = "" }) {
  const [balance, setBalance] = useState(null);
  const [delta, setDelta] = useState(0);
  const lastSeen = useRef(null);
  const deltaTimer = useRef(null);

  useEffect(() => {
    let alive = true;
    const fetchNow = async () => {
      try {
        const r = await axios.get(`${API}/chits/state`);
        if (!alive) return;
        const next = Number.parseInt(r.data?.balance) || 0;
        if (lastSeen.current != null && next > lastSeen.current) {
          const diff = next - lastSeen.current;
          setDelta(diff);
          if (deltaTimer.current) clearTimeout(deltaTimer.current);
          deltaTimer.current = setTimeout(() => setDelta(0), 4000);
        }
        lastSeen.current = next;
        setBalance(next);
      } catch (e) { /* swallow — pill stays hidden if endpoint fails */ }
    };
    fetchNow();
    const interval = setInterval(fetchNow, 8000);
    return () => {
      alive = false;
      clearInterval(interval);
      if (deltaTimer.current) clearTimeout(deltaTimer.current);
    };
  }, []);

  if (balance == null) return null;

  return (
    <div
      data-testid="header-chits-pill"
      className={`relative flex items-center gap-1 border border-zinc-800 px-2 py-1.5 bg-black/40 text-xs font-bold ${className}`}
      title="Chits — earn from harvest, arena, daily login"
    >
      <span className="text-base leading-none">🪙</span>
      <span className="tabular-nums text-amber-300">{balance}</span>
      {delta > 0 && (
        <span
          data-testid="header-chits-delta"
          className="absolute -top-2 -right-2 text-[9px] font-black px-1.5 py-0.5 rounded-full bg-fuchsia-500 text-white animate-pulse shadow-[0_0_8px_rgba(217,70,239,0.6)] leading-none"
        >
          +{delta}
        </span>
      )}
    </div>
  );
}
